import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ChatResponseMetricStatus } from "@fitness-app/data-models/entities/ChatResponseMetric";

import { chatApi, supabase } from "~/store/initializeStore";

export interface ChannelResponseMetric {
  channelId: string;
  status: ChatResponseMetricStatus;
  deadlineAt: number | null;
  createdAt: string;
}

export type ResponseUrgency = "green" | "warning" | "red" | null;

export interface ChannelMetricInfo {
  status: ChatResponseMetricStatus;
  deadlineAt: number | null;
  timeLeftMinutes: number | null;
  urgency: ResponseUrgency;
  createdAt: string;
}

const getUrgency = (deadlineAt: number | null): { urgency: ResponseUrgency; timeLeftMinutes: number | null } => {
  if (!deadlineAt) {
    return { urgency: null, timeLeftMinutes: null };
  }

  // deadlineAt is in seconds (Unix timestamp), Date.now() is in milliseconds
  const nowSeconds = Math.floor(Date.now() / 1000);
  const timeLeftSeconds = deadlineAt - nowSeconds;
  const timeLeftMinutes = Math.floor(timeLeftSeconds / 60);

  if (timeLeftMinutes < 0) {
    // Already past deadline
    return { urgency: "red", timeLeftMinutes };
  }

  const hoursLeft = timeLeftMinutes / 60;

  if (hoursLeft <= 1) {
    // 1h or less - urgent
    return { urgency: "red", timeLeftMinutes };
  } else if (hoursLeft <= 2) {
    // 1-2h - warning
    return { urgency: "warning", timeLeftMinutes };
  } else {
    // 2h+ - ok
    return { urgency: "green", timeLeftMinutes };
  }
};

export const useChannelResponseMetrics = (channelIds: string[], trainerId?: string) => {
  const sortedChannelIds = [...channelIds].sort();
  return useQuery(
    ["channelResponseMetrics", sortedChannelIds.join(","), trainerId],
    async (): Promise<Record<string, ChannelMetricInfo>> => {
      if (channelIds.length === 0) {
        return {};
      }

      let queryBuilder = supabase
        .from("chat_response_metric")
        .select("channelId, status, deadlineAt, createdAt")
        .in("channelId", channelIds)
        .or(
          `status.eq.${ChatResponseMetricStatus.Pending},and(status.eq.${ChatResponseMetricStatus.Breached},respondedAt.is.null)`,
        )
        .order("createdAt", { ascending: false });

      if (trainerId) {
        queryBuilder = queryBuilder.eq("trainerId", trainerId);
      }

      const { data } = await queryBuilder.throwOnError();

      // Group by channelId and get the most urgent metric per channel
      const byChannel: Record<string, ChannelMetricInfo> = {};

      data?.forEach((metric) => {
        const existing = byChannel[metric.channelId];
        const { urgency, timeLeftMinutes } = getUrgency(metric.deadlineAt);

        const currentInfo: ChannelMetricInfo = {
          status: metric.status as ChatResponseMetricStatus,
          deadlineAt: metric.deadlineAt,
          timeLeftMinutes,
          urgency,
          createdAt: metric.createdAt,
        };

        // If no existing metric for this channel, or if current one is more urgent
        if (!existing) {
          byChannel[metric.channelId] = currentInfo;
        } else {
          // Prioritize breached over pending, then by urgency
          const urgencyPriority: Record<Exclude<ResponseUrgency, null> | "null", number> = {
            red: 3,
            warning: 2,
            green: 1,
            null: 0,
          };

          const existingPriority = urgencyPriority[existing.urgency ?? "null"];
          const currentPriority = urgencyPriority[urgency ?? "null"];

          if (currentPriority > existingPriority) {
            byChannel[metric.channelId] = currentInfo;
          }
        }
      });

      return byChannel;
    },
    {
      refetchInterval: 60000, // Refetch every minute to update time left
      staleTime: 30000,
    },
  );
};

export const useMarkChannelAsResponded = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (channelId: string) => {
      await chatApi.post("/mark-channel-as-responded", { channelId });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["channelResponseMetrics"]);
      },
    },
  );
};

export const sortChannelsByUrgency = <T extends { id: string }>(
  channels: T[],
  metrics: Record<string, ChannelMetricInfo>,
): T[] => {
  const urgencyOrder: Record<Exclude<ResponseUrgency, null> | "null", number> = {
    red: 0,
    warning: 1,
    green: 2,
    null: 3,
  };

  return [...channels].sort((a, b) => {
    const metricA = metrics[a.id];
    const metricB = metrics[b.id];

    const urgencyA = metricA?.urgency ?? null;
    const urgencyB = metricB?.urgency ?? null;

    // First sort by urgency (red first, then warning, then green, then null)
    const urgencyDiff = urgencyOrder[urgencyA ?? "null"] - urgencyOrder[urgencyB ?? "null"];
    if (urgencyDiff !== 0) {
      return urgencyDiff;
    }

    // If same urgency, sort by time left (less time = higher priority)
    const timeA = metricA?.timeLeftMinutes ?? Infinity;
    const timeB = metricB?.timeLeftMinutes ?? Infinity;

    return timeA - timeB;
  });
};
